/** Document commercial currencies (quotation / invoice / receipt). */
export const DOCUMENT_CURRENCIES = ["LAK", "USD", "THB"] as const;
export type DocumentCurrency = (typeof DOCUMENT_CURRENCIES)[number];

export const DOCUMENT_CURRENCY_SCALE: Record<DocumentCurrency, 0 | 2> = {
  /** Whole kip — existing data stays as-is */
  LAK: 0,
  USD: 2,
  THB: 2,
};

export const DOCUMENT_CURRENCY_SYMBOL: Record<DocumentCurrency, string> = {
  LAK: "₭",
  USD: "$",
  THB: "฿",
};

export const DOCUMENT_CURRENCY_LABEL: Record<
  DocumentCurrency,
  { lo: string; en: string }
> = {
  LAK: { lo: "ກີບ", en: "LAK" },
  USD: { lo: "USD", en: "USD" },
  THB: { lo: "THB", en: "THB" },
};

export function isDocumentCurrency(value: unknown): value is DocumentCurrency {
  return (
    typeof value === "string" &&
    (DOCUMENT_CURRENCIES as readonly string[]).includes(value)
  );
}

/** Convert major units (what users type) → integer minor units for storage. */
export function toMinorUnits(
  major: number,
  currency: DocumentCurrency,
): number {
  const scale = DOCUMENT_CURRENCY_SCALE[currency];
  if (!Number.isFinite(major)) return 0;
  if (scale === 0) return Math.round(major);
  return Math.round(major * 10 ** scale);
}

/** Convert stored minor units → major units for display / FormPrice. */
export function toMajorUnits(
  minor: number,
  currency: DocumentCurrency,
): number {
  const scale = DOCUMENT_CURRENCY_SCALE[currency];
  if (!Number.isFinite(minor)) return 0;
  if (scale === 0) return minor;
  return minor / 10 ** scale;
}

export function formatDocumentMoney(
  minorUnits: number,
  currency: DocumentCurrency,
  options?: { locale?: string; language?: "lo" | "en" },
): string {
  const language = options?.language ?? "lo";
  const locale =
    options?.locale ?? (language === "en" ? "en-US" : "lo-LA");
  const scale = DOCUMENT_CURRENCY_SCALE[currency];
  const major = toMajorUnits(minorUnits, currency);
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: scale,
    maximumFractionDigits: scale,
  }).format(major);

  if (language === "en") {
    return `${formatted} ${DOCUMENT_CURRENCY_LABEL[currency].en}`;
  }
  if (currency === "LAK") {
    return `${formatted} ${DOCUMENT_CURRENCY_LABEL.LAK.lo}`;
  }
  return `${formatted} ${DOCUMENT_CURRENCY_SYMBOL[currency]}`;
}

export function emptyCurrencyBuckets(): Record<DocumentCurrency, number> {
  return { LAK: 0, USD: 0, THB: 0 };
}

export function addToCurrencyBucket(
  buckets: Record<DocumentCurrency, number>,
  currency: DocumentCurrency | string | null | undefined,
  amount: number,
): void {
  const key = isDocumentCurrency(currency) ? currency : "LAK";
  buckets[key] = (buckets[key] ?? 0) + amount;
}
